import React, { useEffect, useState } from 'react';
import { authAPI } from '../../services/api';
import { Badge, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiUser } from 'react-icons/fi';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
}

const UsersPage: React.FC = () => {
  const [users, setUsers]       = useState<User[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    username: '', email: '', password: '', role: 'Employee', isActive: true,
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await authAPI.getUsers();
      setUsers(response.data);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setModalMode('add');
    setSelectedUser(null);
    setFormData({ username: '', email: '', password: '', role: 'Employee', isActive: true });
    setShowModal(true);
  };

  const handleOpenEdit = (user: User) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({ username: user.username, email: user.email, password: '', role: user.role, isActive: user.isActive });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (modalMode === 'add') {
        await authAPI.register(formData.username, formData.email, formData.password, formData.role);
        setSuccess('User created successfully');
      } else if (selectedUser) {
        await authAPI.updateUser(selectedUser.id, { email: formData.email, role: formData.role, isActive: formData.isActive });
        setSuccess('User updated successfully');
      }
      fetchUsers();
      setShowModal(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Delete user ${user.username}?`)) return;
    try {
      await authAPI.deleteUser(user.id);
      setSuccess('User deleted');
      fetchUsers();
    } catch {
      setError('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h2>Users</h2>
        <Button variant="primary" className="d-flex align-items-center gap-2" onClick={handleOpenAdd}>
          <FiPlus /> Add User
        </Button>
      </div>

      {error   && <Alert variant="danger"  dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      <div className="section-card">
        <div className="section-card-header">
          <span className="section-card-title"><FiUsers style={{ color: '#16a34a' }} /> All Users ({users.length})</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table mb-0">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <div style={{
                        width: 30, height: 30, borderRadius: '50%',
                        background: user.role === 'Admin' ? '#dcfce7' : '#f0fdf4',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: user.role === 'Admin' ? '#15803d' : '#16a34a',
                        fontSize: '0.8rem', flexShrink: 0,
                      }}>
                        <FiUser />
                      </div>
                      <strong>{user.username}</strong>
                    </div>
                  </td>
                  <td style={{ color: '#374151' }}>{user.email}</td>
                  <td>
                    <Badge bg={user.role === 'Admin' ? 'primary' : 'success'}>
                      {user.role}
                    </Badge>
                  </td>
                  <td>
                    {user.isActive
                      ? <Badge bg="success">Active</Badge>
                      : <Badge bg="danger">Inactive</Badge>}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '0.82rem' }}>
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ color: '#64748b', fontSize: '0.82rem' }}>
                    {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className="action-btn-group">
                      <Button variant="outline-primary" size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => handleOpenEdit(user)}>
                        <FiEdit2 size={11} /> Edit
                      </Button>
                      <Button variant="outline-danger" size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={() => handleDelete(user)}
                        disabled={user.username === 'admin'}>
                        <FiTrash2 size={11} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{modalMode === 'add' ? 'Add New User' : 'Edit User'}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 py-3">
            <div className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control type="text" value={formData.username} required disabled={modalMode === 'edit'}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })} />
            </div>
            <div className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={formData.email} required
                onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            {modalMode === 'add' && (
              <div className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" value={formData.password} required
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
              </div>
            )}
            <div className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                <option value="Employee">Employee</option>
                <option value="Admin">Admin</option>
              </Form.Select>
            </div>
            {modalMode === 'edit' && (
              <Form.Check type="switch" id="isActive" label="Active"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit">
              {modalMode === 'add' ? 'Create User' : 'Update User'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default UsersPage;
